import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { winesApi, producersApi } from '../../lib/api';
import { DataTable, Pagination } from '../../components/ui/DataTable';
import { MaturityStateBadge } from '../../components/ui/StatusBadge';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '../../lib/utils';
const WINE_COLOURS = [
    { value: '', label: 'All colours' },
    { value: 'red', label: 'Red' },
    { value: 'white', label: 'White' },
    { value: 'rose', label: 'Rosé' },
    { value: 'sparkling', label: 'Sparkling' },
    { value: 'fortified', label: 'Fortified' },
    { value: 'dessert', label: 'Dessert' },
    { value: 'orange', label: 'Orange' },
];
function ColourDot({ colour }) {
    const colours = {
        red: 'bg-red-700',
        white: 'bg-yellow-200 border border-yellow-300',
        rose: 'bg-pink-400',
        sparkling: 'bg-yellow-100 border border-yellow-200',
        fortified: 'bg-amber-700',
        dessert: 'bg-amber-400',
        orange: 'bg-orange-400',
    };
    return (_jsx("span", { className: cn('inline-block h-3 w-3 rounded-full flex-shrink-0', colours[colour] ?? 'bg-gray-300'), title: colour }));
}
const columns = [
    {
        key: 'name',
        header: 'Wine',
        render: (row) => (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(ColourDot, { colour: row.colour }), _jsxs("div", { children: [_jsx("p", { className: "font-medium text-gray-900", children: row.producerName }), _jsx("p", { className: "text-sm text-gray-600", children: row.name })] })] })),
    },
    {
        key: 'region',
        header: 'Region',
        render: (row) => row.regionName ?? '—',
        className: 'text-gray-600',
    },
    {
        key: 'drinkingWindow',
        header: 'Drinking Window',
        render: (row) => {
            if (!row.drinkFrom && !row.drinkTo)
                return '—';
            const peak = row.peakStart && row.peakEnd ? ` (peak ${row.peakStart}–${row.peakEnd})` : '';
            return `${row.drinkFrom ?? '?'}–${row.drinkTo ?? '?'}${peak}`;
        },
        className: 'text-gray-600 text-xs',
    },
    {
        key: 'maturity',
        header: 'Maturity',
        render: (row) => _jsx(MaturityStateBadge, { status: row.effectiveMaturityState }),
    },
    {
        key: 'bottles',
        header: 'Bottles',
        render: (row) => (_jsx("span", { className: "font-medium tabular-nums", children: row.bottleCount })),
        className: 'w-20 text-right',
        headerClassName: 'text-right',
    },
];
export function WineListPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { canEdit } = useAuth();
    const [search, setSearch] = useState('');
    const [colour, setColour] = useState('');
    const [page, setPage] = useState(1);
    // When arriving from the Producers list the URL carries ?producerId=xxx
    const producerIdFilter = searchParams.get('producerId') ?? undefined;
    // Fetch the producer name to display as a heading when filtering
    const { data: producerData } = useQuery({
        queryKey: ['producer', producerIdFilter],
        queryFn: () => producersApi.get(producerIdFilter),
        enabled: Boolean(producerIdFilter),
    });
    const producerName = producerData?.name;
    const { data, isLoading } = useQuery({
        queryKey: ['wines', search, colour, producerIdFilter, page],
        queryFn: () => winesApi.list({
            search: search || undefined,
            colour: colour || undefined,
            producerId: producerIdFilter,
            page,
        }),
    });
    const addWineUrl = producerIdFilter
        ? `/wines/new?producerId=${producerIdFilter}`
        : '/wines/new';
    const clearProducerFilter = () => {
        setSearchParams({});
        setPage(1);
    };
    return (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("h1", { className: "page-title", children: "Wines" }), producerName && (_jsxs("p", { className: "text-sm text-gray-500 mt-0.5", children: ["Showing wines by ", _jsx("span", { className: "font-medium text-gray-700", children: producerName }), ' ', "\u00B7", ' ', _jsx("button", { onClick: clearProducerFilter, className: "text-wine-700 hover:underline", children: "Show all" })] }))] }), canEdit && (_jsxs("button", { onClick: () => navigate(addWineUrl), className: "btn-primary", children: [_jsx(Plus, { className: "h-4 w-4" }), producerName ? `Add wine by ${producerName}` : 'Add wine'] }))] }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsxs("div", { className: "relative flex-1 max-w-sm", children: [_jsx(Search, { className: "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" }), _jsx("input", { type: "search", placeholder: "Search wines or producers\u2026", value: search, onChange: (e) => { setSearch(e.target.value); setPage(1); }, className: "input pl-9" })] }), _jsx("select", { value: colour, onChange: (e) => { setColour(e.target.value); setPage(1); }, className: "select w-40", children: WINE_COLOURS.map((c) => (_jsx("option", { value: c.value, children: c.label }, c.value))) })] }), _jsxs("div", { className: "card overflow-hidden", children: [_jsx(DataTable, { columns: columns, data: data?.wines ?? [], isLoading: isLoading, rowKey: (row) => row.id, onRowClick: (row) => navigate(`/wines/${row.id}`), emptyTitle: "No wines found", emptyDescription: search || producerIdFilter
                            ? 'Try adjusting your search or filters'
                            : 'Add your first wine to get started' }), _jsx(Pagination, { page: page, pageSize: 50, total: data?.total ?? 0, onPageChange: setPage })] })] }));
}
